import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, notification, Space, theme, Typography } from 'antd';
import React, { useMemo } from 'react';
import { useArchiveMediasToQualify } from '../../../../../utils/api/medias/useArchiveMediasToQualify';
import { useUnarchiveMediasToQualify } from '../../../../../utils/api/medias/useUnarchiveMediasToQualify';
import { useUpdateStatusMediasToQualify } from '../../../../../utils/api/medias/useUpdateStatusMediasToQualify';

const { Text } = Typography;

const mediaCountLabel = (count) =>
  count <= 1 ? `${count} média` : `${count} médias`;

const MediaActions = ({
  medias = [],
  selectedMediaIds = [],
  displayedStatus = null,
  onClearSelection,
  resolveBulkActions = null,
  totalCount,
  setHasRejectedMedias,
  isLoading,
}) => {
  const { token } = theme.useToken();

  const { updateStatusMediasToQualify } = useUpdateStatusMediasToQualify();
  const { archiveMediasToQualify } = useArchiveMediasToQualify();
  const { unarchiveMediasToQualify } = useUnarchiveMediasToQualify();

  const defaultResolveBulkActions = (displayedStatus, ctx) => {
    const { clearSelection, token, selectedMediaIds } = ctx;

    const cancel = {
      label: 'Annuler',
      type: 'link',
      style: token ? { color: token.colorTextSecondary } : undefined,
      onClick: clearSelection,
    };

    const handleAction = (messageName, options = {}) => {
      const {
        label,
        type = 'default',
        danger,
        style,
        icon,
        disabled,
        action,
      } = options;
      return {
        label,
        type,
        danger,
        style,
        icon,
        disabled,
        onClick: async () => {
          switch (action) {
            case 'send_to_validate':
              try {
                const result = await updateStatusMediasToQualify({
                  status: 'to_validate',
                  mediaIds: selectedMediaIds,
                });

                if (result?.errors?.length > 0) {
                  throw new Error(result.errors.map((e) => e.error).join('; '));
                }

                notification.success({
                  message: 'Envoi en validation effectués',
                  description: 'Les modifications ont été enregistrées',
                  placement: 'bottomRight',
                  duration: 3,
                });
                clearSelection();
              } catch (e) {
                notification.error({
                  message: "Erreur d'enregistrement",
                  description: `Les modifications n'ont pas pu être enregistrées : ${e}`,
                  placement: 'bottomRight',
                  duration: 3,
                });
              }
              break;
            case 'validate':
              try {
                const result = await updateStatusMediasToQualify({
                  status: 'validated',
                  mediaIds: selectedMediaIds,
                });

                if (result?.errors?.length > 0) {
                  throw new Error(result.errors.map((e) => e.error).join('; '));
                }

                notification.info({
                  message: 'Validation des médias',
                  description:
                    'Les médias sont en cours de validation. Vous recevrez un mail quand les médias seront validés.',
                  placement: 'bottomRight',
                  duration: 3,
                });
                clearSelection();
              } catch (e) {
                notification.error({
                  message: 'Erreur de validation des médias',
                  description: `Les médias n'ont pas pu être validés : ${e}`,
                  placement: 'bottomRight',
                  duration: 3,
                });
              }
              break;
            case 'reject':
              try {
                setHasRejectedMedias(true);
              } catch (e) {
                notification.error({
                  message: 'Erreur de refus des médias',
                  description: `Les médias n'ont pas pu être refusés : ${e}`,
                  placement: 'bottomRight',
                  duration: 3,
                });
              }
              break;
            case 'archive':
              try {
                await archiveMediasToQualify({
                  mediaIds: selectedMediaIds,
                });

                notification.info({
                  message: 'Archivage des médias',
                  description: 'Les médias ont bien été archivés.',
                  placement: 'bottomRight',
                  duration: 3,
                });
                clearSelection();
              } catch (e) {
                notification.error({
                  message: "Erreur d'archivage des médias",
                  description: `Les médias n'ont pas pu être archivés : ${e.response?.data?.non_field_errors?.join('\n') || e}`,
                  placement: 'bottomRight',
                  duration: 3,
                });
              }
              break;
            case 'unarchive':
              try {
                await unarchiveMediasToQualify({
                  mediaIds: selectedMediaIds,
                });

                notification.info({
                  message: 'Désarchivage des médias',
                  description: 'Les médias ont bien été désarchivés.',
                  placement: 'bottomRight',
                  duration: 3,
                });
                clearSelection();
              } catch (e) {
                notification.error({
                  message: 'Erreur de désarchivage des médias',
                  description: `Les médias n'ont pas pu être désarchivés : ${e}`,
                  placement: 'bottomRight',
                  duration: 3,
                });
              }
              break;
          }
        },
      };
    };

    const bulkArchiveAction = handleAction('Archivage', {
      label: 'Archiver',
      type: 'default',
      icon: <DeleteOutlined />,
      style: token ? { color: token.colorTextSecondary } : undefined,
      action: 'archive',
    });

    switch (displayedStatus) {
      case 'to_qualify':
        return {
          primary: handleAction('Envoi en validation', {
            label: 'Envoyer en validation',
            type: 'primary',
            icon: <FileSearchOutlined />,
            action: 'send_to_validate',
          }),
          secondary: handleAction('Rejeter', {
            label: 'Rejeter',
            type: 'default',
            icon: <CloseCircleOutlined />,
            danger: true,
            action: 'reject',
          }),
          archive: bulkArchiveAction,
          cancel,
        };
      case 'to_validate':
        return {
          primary: handleAction('Validation', {
            label: 'Valider',
            type: 'primary',
            style: { backgroundColor: '#52c41a' },
            icon: <CheckCircleOutlined />,
            action: 'validate',
          }),
          secondary: handleAction('Rejet', {
            label: 'Rejeter',
            type: 'default',
            danger: true,
            icon: <CloseCircleOutlined />,
            action: 'reject',
          }),
          archive: bulkArchiveAction,
          cancel,
        };
      case 'rejected':
        return {
          primary: null,
          secondary: null,
          archive: bulkArchiveAction,
          cancel,
        };
      case 'archived':
        return {
          primary: handleAction('Restauration', {
            label: 'Restaurer',
            type: 'primary',
            action: 'unarchive',
          }),
          secondary: null,
          archive: null,
          cancel,
        };
      default:
        return {
          primary: null,
          secondary: null,
          archive: bulkArchiveAction,
          cancel,
        };
    }
  };

  if (!resolveBulkActions) {
    resolveBulkActions = defaultResolveBulkActions;
  }

  const selectedInViewCount = useMemo(() => {
    const set = new Set(selectedMediaIds);
    return medias.reduce((acc, m) => acc + (set.has(m.id) ? 1 : 0), 0);
  }, [medias, selectedMediaIds]);

  const hasSelection = selectedMediaIds.length > 0;

  const bulkConfig = useMemo(() => {
    if (!hasSelection || !onClearSelection) return null;
    return resolveBulkActions(displayedStatus, {
      selectedCount: selectedMediaIds.length,
      totalCount,
      clearSelection: onClearSelection,
      token,
      selectedMediaIds: selectedMediaIds,
    });
  }, [
    displayedStatus,
    hasSelection,
    medias,
    onClearSelection,
    resolveBulkActions,
    selectedInViewCount,
    selectedMediaIds,
    token,
    totalCount,
  ]);

  const countBlock = (() => {
    if (hasSelection) {
      return (
        <Text strong>
          {selectedMediaIds.length}/{totalCount}
        </Text>
      );
    }

    if (isLoading) {
      return (
        <Text type="secondary">
          <LoadingOutlined aria-label="Chargement en cours" />{' '}
          <span>chargement des médias...</span>
        </Text>
      );
    }

    return <Text type="secondary">{mediaCountLabel(totalCount)}</Text>;
  })();

  const hasBulkButtons =
    bulkConfig &&
    (bulkConfig.primary ||
      bulkConfig.secondary ||
      bulkConfig.tertiary ||
      bulkConfig.archive ||
      bulkConfig.cancel);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        padding: '8px 16px',
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <div>{countBlock}</div>
      {hasSelection && hasBulkButtons && (
        <Space size="small" wrap>
          {bulkConfig.primary && (
            <Button
              type={bulkConfig.primary.type ?? 'primary'}
              disabled={bulkConfig.primary.disabled}
              danger={bulkConfig.primary.danger}
              icon={bulkConfig.primary.icon}
              style={bulkConfig.primary.style}
              onClick={bulkConfig.primary.onClick}
              size="small"
            >
              {bulkConfig.primary.label}
            </Button>
          )}
          {bulkConfig.secondary && (
            <Button
              type={bulkConfig.secondary.type ?? 'default'}
              disabled={bulkConfig.secondary.disabled}
              danger={bulkConfig.secondary.danger}
              icon={bulkConfig.secondary.icon}
              style={bulkConfig.secondary.style}
              onClick={bulkConfig.secondary.onClick}
              size="small"
            >
              {bulkConfig.secondary.label}
            </Button>
          )}
          {bulkConfig.tertiary && (
            <Button
              type={bulkConfig.tertiary.type ?? 'default'}
              disabled={bulkConfig.tertiary.disabled}
              danger={bulkConfig.tertiary.danger}
              icon={bulkConfig.tertiary.icon}
              style={bulkConfig.tertiary.style}
              onClick={bulkConfig.tertiary.onClick}
              size="small"
            >
              {bulkConfig.tertiary.label}
            </Button>
          )}
          {bulkConfig.archive && (
            <Button
              type={bulkConfig.archive.type ?? 'default'}
              disabled={bulkConfig.archive.disabled}
              danger={bulkConfig.archive.danger}
              icon={bulkConfig.archive.icon}
              style={bulkConfig.archive.style}
              onClick={bulkConfig.archive.onClick}
              size="small"
            >
              {bulkConfig.archive.label}
            </Button>
          )}
          {bulkConfig.cancel && (
            <Button
              type={bulkConfig.cancel.type ?? 'default'}
              disabled={bulkConfig.cancel.disabled}
              style={bulkConfig.cancel.style}
              onClick={bulkConfig.cancel.onClick ?? onClearSelection}
              size="small"
            >
              {bulkConfig.cancel.label ?? 'Annuler'}
            </Button>
          )}
        </Space>
      )}
    </div>
  );
};

export default MediaActions;
